<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-item-list">
        <i class="far fa-list"></i> Order Item List
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-item-list">
        <DataTable
          ref="dataTable"
          :columns="columnList"
          defaultColumnOrderDir="ASC"
          :defaultColumnOrderIndex="0"
          :url="dataURL"
          :filterOptions="orderItemFilterOptions"
          :displayTools="true"
          :hasDateRangeFilter="true"
          :displayExportTools="true"
          :useReportingServer="true"
          :blockFiltersWhileLoading="true"
          exportTitle="Order Items"
          :debounceTime="2000"
        >
          <template v-slot:title>
            <slot name="title" />
          </template>
          <template v-slot:tools>
            <slot name="tools" />
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td>
                {{row.data.id}}
              </td>
              <td>
                <LabelBadge :labelClass="`label-stock-${row.data.stockType}`">{{formatLookupName(row.data.stockType, InventoryStockTypeLookup)}}</LabelBadge>
              </td>
              <td>
                <Tooltip :label="row.data.stockName"><LinkStock :id="row.data.stockId" :name="row.data.stockCode" /></Tooltip>
              </td>
              <td>
                {{row.data.quantity}}
              </td>
              <td>
                <span v-if="row.data.stockType == InventoryStockTypes.glass || row.data.stockType == InventoryStockTypes.template">
                  {{row.data.glassHeight}} x {{row.data.glassWidth}}
                </span>
                <span v-else>
                  -
                </span>
              </td>
              <td>{{row.data.generatedDescription}}</td>
              <td>{{formatBoolean(row.data.isRootItem)}}</td>
              <td>{{formatBoolean(row.data.isProcessedItem)}}</td>
              <td>{{formatBoolean(row.data.isCancelled)}}</td>

              <td>
                <LinkOrder :id="row.data.orderId" :name="row.data.orderSequenceNumber" />
              </td>
              <td>
                <LabelBadge :labelClass="`label-order-type-${row.data.orderType}`">{{row.data.orderType | formatLookupName(OrderTypeLookup)}}</LabelBadge>
              </td>
              <td>
                <LabelOrderStatusBadge :status="row.data.orderStatus" />
              </td>
              <td>{{formatDateTime(row.data.orderDateOrderCreated)}}</td>
              <td>{{formatDateTime(row.data.orderDateClosed)}}</td>
              <td>
                <LinkContact :id="row.data.orderContactId" :name="row.data.orderContactName" />
              </td>
              <td>
                <LabelBadge :labelClass="`label-approval-${row.data.ncrApprovalStatus}`" v-if="row.data.orderType == OrderTypes.nonChargeableRework">{{formatLookupName(row.data.ncrApprovalStatus, ApprovalTypeLookup)}}</LabelBadge>
                <span v-if="row.data.orderType != OrderTypes.nonChargeableRework">-</span>
              </td>
              <td>
                {{row.data.ncrApprovalAcceptedQuantity}}
              </td>
              <td>
                {{row.data.ncrApprovalRejectedQuantity}}
              </td>
            </tr>
          </template>
          <template v-slot:summary="summary">
            <tr key="summary">
              <td>Totals</td>
              <td colspan="2"></td>
              <td>{{summary.data.totalQuantity}}</td>
              <td colspan="12"></td>
              <td>{{summary.data.totalNCRAcceptedQuantity}}</td>
              <td>{{summary.data.totalNCRRejectedQuantity}}</td>
            </tr>
          </template>
        </DataTable>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'

import moment from 'moment'
import buildUrl from 'build-url'
import { mapState } from 'vuex'

import LabelOrderStatusBadge from '@/modules/orders/components/labelOrderStatusBadge'

import Utils from '@/utils/utils'
import { OrderTypeLookup, InventoryStockTypes, InventoryStockTypeLookup, ApprovalTypes, ApprovalTypeLookup, OrderStatusTypeLookup, OrderStatusTypes, OrderTypes } from '@/store/types'

export default {
  name: 'OrderItems',
  components: {
    LabelOrderStatusBadge
  },
  data() {
    return {
      columnList: [
        { title: "ID", data: "id" },
        { title: "Type", data: "stockType" },
        { title: "Stock", data: "stockName" },
        { title: "Quantity", data: "quantity" },
        { title: "Size", data: "size" },
        { title: 'Extra Info', data: 'generatedDescription' },
        { title: 'Root Item', data: 'isRootItem', type: 'boolean' },
        { title: 'Processed Item', data: 'isProcessedItem', type: 'boolean' },
        { title: 'Cancelled', data: 'isCancelled', type: 'boolean' },

        { title: "Order", data: "orderSequenceNumber" },
        { title: "Order Type", data: "orderType" },
        { title: "Order Status", data: "orderStatus" },
        { title: "Order Created", data: "orderDateOrderCreated", dateFilter: true, type: 'datetime' },
        { title: "Order Completed", data: "orderDateClosed", dateFilter: true, type: 'datetime' },
        { title: "Customer", data: "orderContactName" },

        { title: "NCR Status", data: "ncrApprovalStatus" },
        { title: "NCR Accepted", data: "ncrApprovalAcceptedQuantity" },
        { title: "NCR Rejected", data: "ncrApprovalRejectedQuantity" }        
      ]
    }
  },
  computed: {
    ...mapState({
      taxName: state => state.account.salesTaxRate.name
    }),
    dataURL() {
      var params = {
        filter: {},
        includesummary: true
      }      

      return '/orders/orderitems?' + $.param(params)
    },
    orderItemFilterOptions() {
      var filterOptions = [
        {
          field: 'orderStatus',
          name: 'Order Status',
          options: [
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.draft), value: OrderStatusTypes.draft, class: 'label-order-status-draft' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingAccountApproval), value: OrderStatusTypes.pendingAccountApproval, class: 'label-order-status-pendingAccountApproval' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingProductionApproval), value: OrderStatusTypes.pendingProductionApproval, class: 'label-order-status-pendingProductionApproval' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.quotePendingSent), value: OrderStatusTypes.quotePendingSent, class: 'label-order-status-quotePendingSent' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingCustomerApproval), value: OrderStatusTypes.pendingCustomerApproval, class: 'label-order-status-pendingCustomerApproval' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingPrePaid), value: OrderStatusTypes.pendingPrePaid, class: 'label-order-status-pendingPrePaid' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingExternalSetup), value: OrderStatusTypes.pendingExternalSetup, class: 'label-order-status-pendingExternalSetup' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.preProduction), value: OrderStatusTypes.preProduction, class: 'label-order-status-preProduction' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.production), value: OrderStatusTypes.production, class: 'label-order-status-production' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingCODPaid), value: OrderStatusTypes.pendingCODPaid, class: 'label-order-status-pendingCODPaid' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.despatch), value: OrderStatusTypes.despatch, class: 'label-order-status-despatch' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.ncrPendingReturn), value: OrderStatusTypes.ncrPendingReturn, class: 'label-order-status-ncrPendingReturn' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.completed), value: OrderStatusTypes.completed, class: 'label-order-status-completed' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.denied), value: OrderStatusTypes.denied, class: 'label-order-status-denied' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.cancelled), value: OrderStatusTypes.cancelled, class: 'label-order-status-cancelled' }            
          ]
        },
        {
          field: 'orderType',
          name: 'Order Type',
          columnIndex: 1,
          options: [
            { name: 'Quote', value: OrderTypes.quote, class: 'label-order-type-quote' },
            { name: 'Order', value: OrderTypes.order, class: 'label-order-type-order' },
            { name: 'NCR', value: OrderTypes.nonChargeableRework, class: 'label-order-type-nonChargeableRework' }
          ]
        },
        {
          field: 'stockType',
          name: 'Stock Type',
          columnIndex: 1,
          options: [
            { name: 'Glass', value: 'glass', class: 'label-stock-glass' },
            { name: 'Glass Service', value: 'glassService', class: 'label-stock-glassService' },
            { name: 'Assembly', value: 'assembly', class: 'label-stock-assembly' },
            { name: 'Template', value: 'template', class: 'label-stock-template' },
            { name: 'General', value: 'general', class: 'label-stock-general' },
          ]
        },
        {
          field: 'isCancelled',
          name: 'Item Cancelled',
          columnIndex: 2,
          options: [
            { name: 'Is Cancelled', value: 1, class: 'label-order-status-cancelled' },
            { name: 'Not Cancelled', value: 0, class: 'label-order-status-cancelled' }
          ]
        },
        {
          field: 'isRootItem',
          name: 'Root Item',
          columnIndex: 2,
          options: [
            { name: 'Is Root Item', value: 1, class: 'label-stock-glass' }
          ]
        },
        {
          field: 'isProcessedItem',
          name: 'Processed Item',
          columnIndex: 2,
          options: [
            { name: 'Is Processed Item', value: 1, class: 'label-order-status-production' },
            { name: 'Not Processed Item', value: 0, class: 'label-order-status-production' }
          ]
        },
        {
          field: 'ncrApprovalStatus',
          name: 'NCR Approval Status',
          columnIndex: 2,
          options: [
            { name: Utils.getLookupName(ApprovalTypeLookup, ApprovalTypes.pending), value: ApprovalTypes.pending, class: 'label-approval-pending' },
            { name: Utils.getLookupName(ApprovalTypeLookup, ApprovalTypes.approved), value: ApprovalTypes.approved, class: 'label-approval-approved' },
            { name: Utils.getLookupName(ApprovalTypeLookup, ApprovalTypes.denied), value: ApprovalTypes.denied, class: 'label-approval-denied' },
          ]
        }    
      ]

      return filterOptions
    }
  },
  created() {
    this.ApprovalTypes = ApprovalTypes
    this.ApprovalTypeLookup = ApprovalTypeLookup
    this.OrderTypeLookup = OrderTypeLookup
    this.OrderTypes = OrderTypes
    this.InventoryStockTypes = InventoryStockTypes
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
  },
  mounted() {
  },
  beforeDestroy() {
  },
  methods: {
    
  }
}
</script>
